import type { FastifyInstance, FastifyRequest } from 'fastify';
import { isValid as isValidULID } from 'ulid';
import { isPrivateIP } from 'range_check';

/**
 * Normalized client identity for a request, resolved by the server before plugins run.
 *
 * The canonical request accessors are `request.clientIP` (the real end user),
 * `request.connectionIP` (the connecting IP), `request.userAgent` (the
 * immediate-hop User-Agent), and `request.clientUserAgent` (the resolved real
 * end-user User-Agent). This frozen object mirrors the resolved end-user values
 * alongside correlation and forwarding metadata. `isIPFromHeader` is true when
 * `clientIP` came from a trusted `X-SSR-Original-IP` header rather than the
 * connection.
 */
export interface ClientInfo {
  requestID: string; // Unique ID for this specific request (mirrors request.requestID)
  correlationID: string; // ID for tracing across services (forwarded value, or the request ID)
  /** True when request came from SSR layer with trusted forwarded headers */
  isFromSSRServerAPICall: boolean;
  /** The connecting IP (mirrors request.connectionIP). */
  connectionIP: string;
  /** Resolved real end-user IP (mirrors request.clientIP). */
  clientIP: string;
  userAgent: string;
  /** True when clientIP was taken from a trusted X-SSR-Original-IP header */
  isIPFromHeader: boolean;
  /** True when userAgent was taken from a trusted X-SSR-Forwarded-User-Agent header */
  isUserAgentFromHeader: boolean;
}

// Extend FastifyRequest to include client info.
// NOTE: `requestID`, `connectionIP`, and `clientIP` are declared by the core
// framework (see types.ts); they are set by the server, not here.
declare module 'fastify' {
  interface FastifyRequest {
    clientInfo?: ClientInfo;
  }
}

/**
 * Optional logging for the client-info resolution step.
 */
export interface ClientInfoLoggingOptions {
  /** Log each request with its request ID. Default: false */
  requestReceived?: boolean;
  /** Log decision/details when trusting forwarded client info. Default: false */
  forwardedClientInfo?: boolean;
  /** Warn when SSR/forwarded headers are present from an untrusted source. Default: false */
  rejectedForwardedHeaders?: boolean;
}

/**
 * Configuration for client-info resolution (the `clientInfo` server
 * option). Pass `false` for the option to disable resolution entirely.
 */
export interface ClientInfoConfig {
  /**
   * Custom validator for forwarded correlation IDs (the `X-Correlation-ID`
   * header from trusted SSR requests). Forwarded values that fail validation
   * are ignored and the request ID is used as the correlation ID instead.
   * Defaults to ULID validation.
   *
   * Note: the request ID itself is generated by the server (see the
   * `getRequestID` server option).
   */
  forwardedRequestIDValidator?: (id: string) => boolean;
  /** If true, set X-Request-ID and X-Correlation-ID response headers. Default: true */
  setResponseHeaders?: boolean;
  /**
   * Whether to accept forwarded client-info headers (`X-SSR-Request`,
   * `X-SSR-Original-IP`, `X-SSR-Forwarded-User-Agent`, `X-Correlation-ID`) from
   * an upstream SSR server. When trusted, they override `request.clientIP` /
   * `clientInfo`; when not, the direct connection values are used.
   *
   * - `false` / unset (default): never trust forwarded headers — secure by
   *   default. Prevents a client from spoofing its IP/UA through a proxy.
   * - `'local'`: trust when `request.connectionIP` is a private/internal
   *   address. Convenient for an SSR → API hop, but a private-IP proxy that
   *   forwards client headers can spoof this, so only use it when the API is
   *   network-isolated to your SSR tier (or set `fastifyOptions.trustProxy` so
   *   `connectionIP` reflects the real external client).
   * - `true`: always trust (the API is only reachable by your SSR tier).
   * - function: custom per-request decision.
   *
   * @default false
   */
  trustForwardedHeaders?:
    | boolean
    | 'local'
    | ((request: FastifyRequest) => boolean);
  /** Optional logging configuration */
  logging?: boolean | ClientInfoLoggingOptions;
}

/**
 * Registers the client-identity resolution step.
 *
 * Runs in an early onRequest hook (after connection-IP and request-ID
 * decoration, before access logging) so `request.clientIP` and
 * `request.clientInfo` reflect the real end user — recovering the original
 * browser IP / User-Agent / correlation ID from trusted forwarded SSR headers.
 *
 * It:
 * 1. Resolves the real end-user IP into `request.clientIP` (overriding the
 *    connecting IP when a trusted `X-SSR-Original-IP` is present).
 * 2. Populates a frozen `request.clientInfo` with correlation/forwarding metadata.
 * 3. Only trusts forwarded headers from private/allowed sources.
 */
export function registerClientInfoResolution(
  fastify: FastifyInstance,
  config: ClientInfoConfig = {},
): void {
  fastify.decorateRequest('clientInfo', undefined);

  const validateForwardedRequestID =
    typeof config.forwardedRequestIDValidator === 'function'
      ? config.forwardedRequestIDValidator
      : (id: string) => isValidULID(id);

  fastify.addHook('onRequest', async (request, reply) => {
    const loggingConfig = config.logging;
    const shouldLogAll = loggingConfig === true;
    const shouldLogNone =
      loggingConfig === false || typeof loggingConfig === 'undefined';
    const loggingObject: ClientInfoLoggingOptions | undefined =
      typeof loggingConfig === 'object' && loggingConfig !== null
        ? loggingConfig
        : undefined;

    const shouldLogRequestReceived =
      shouldLogAll ||
      (!shouldLogNone && loggingObject?.requestReceived === true);
    const shouldLogForwardedClientInfo =
      shouldLogAll ||
      (!shouldLogNone && loggingObject?.forwardedClientInfo === true);
    const shouldLogRejectedForwardedHeaders =
      shouldLogAll ||
      (!shouldLogNone && loggingObject?.rejectedForwardedHeaders === true);

    // Request ID generated by the server (defaults to a ULID, or the
    // getRequestID server option). Empty string only if getRequestID opted out.
    const requestID = request.requestID ?? '';

    // Resolved end-user User-Agent (direct value by default, seeded by server-utils)
    let userAgent = request.clientUserAgent;
    let isUserAgentFromHeader = false;

    let isIPFromHeader = false;
    let isFromSSRServerAPICall = false;
    let correlationID: string | null = null;

    // Decide whether to trust forwarded headers. Secure by default (deny):
    // 'local' trusts private connecting IPs, true always trusts, a function is
    // custom, and false/unset never trusts.
    const trust = config.trustForwardedHeaders;
    let shouldTrustForwarded: boolean;

    if (typeof trust === 'function') {
      shouldTrustForwarded = trust(request) === true;
    } else if (trust === 'local') {
      shouldTrustForwarded = isPrivateIP(request.connectionIP);
    } else {
      shouldTrustForwarded = trust === true;
    }

    // Presence of any forwarded SSR header — computed once and used by both the
    // trust branch and the spoof-rejection branch below (keep them in sync).
    const ssrHeader = request.headers['x-ssr-request'];
    const isSSRRequest = typeof ssrHeader === 'string' && ssrHeader === 'true';
    const hasForwardedHeaders =
      isSSRRequest ||
      !!request.headers['x-ssr-original-ip'] ||
      !!request.headers['x-ssr-forwarded-user-agent'] ||
      !!request.headers['x-correlation-id'];

    if (shouldTrustForwarded) {
      // X-Correlation-ID is a standard cross-service tracing header, not an
      // SSR-specific concept — honor a valid forwarded value from any trusted
      // source (even without X-SSR-Request), so non-SSR upstreams can propagate
      // a trace ID. Still gated by trust + the validator (ULID by default).
      const correlationHeader = request.headers['x-correlation-id'];

      if (
        typeof correlationHeader === 'string' &&
        validateForwardedRequestID(correlationHeader)
      ) {
        correlationID = correlationHeader;
      }

      // SSR identity recovery (the original client IP / forwarded User-Agent /
      // isFromSSRServerAPICall) is SSR-specific, so it requires the explicit
      // X-SSR-Request marker (the SSR server always sets it when forwarding). A
      // trusted source that didn't mark the request as SSR does not override
      // clientIP/UA.
      if (isSSRRequest) {
        isFromSSRServerAPICall = true;

        // Recover the real end-user IP into request.clientIP
        const originalIPHeader = request.headers['x-ssr-original-ip'];

        if (typeof originalIPHeader === 'string') {
          request.clientIP = originalIPHeader;
          isIPFromHeader = true;
        }

        const forwardedUserAgentHeader =
          request.headers['x-ssr-forwarded-user-agent'];

        if (typeof forwardedUserAgentHeader === 'string') {
          userAgent = forwardedUserAgentHeader;
          request.clientUserAgent = forwardedUserAgentHeader;
          isUserAgentFromHeader = true;
        }

        if (shouldLogForwardedClientInfo) {
          request.log?.debug?.(
            {
              requestID,
              correlationID,
              clientIP: request.clientIP,
              connectionIP: request.connectionIP,
              isFromSSRServerAPICall,
            },
            'client-info: trusted forwarded SSR headers — recovered the original client IP/User-Agent',
          );
        }
      }
    } else if (hasForwardedHeaders) {
      // SSR headers present but the connection isn't trusted (possible spoof).
      if (shouldLogRejectedForwardedHeaders) {
        request.log?.warn?.(
          {
            requestID,
            connectionIP: request.connectionIP,
          },
          'client-info: ignored forwarded SSR headers from an untrusted connection (possible spoofing attempt)',
        );
      }
    }

    // For direct requests, the request ID doubles as the correlation ID.
    // Direct requests (and the getRequestID opt-out) fall back to the request
    // ID, so the resolved correlation ID is always a string (possibly '' when
    // the request ID opted out) — never null.
    const resolvedCorrelationID: string = correlationID ?? requestID;

    // Optionally add the request/correlation IDs to response headers. Guard
    // against empty values (when getRequestID opts out) — don't emit blank headers.
    if (config.setResponseHeaders !== false) {
      if (requestID) {
        reply.header('X-Request-ID', requestID);
      }

      if (resolvedCorrelationID) {
        reply.header('X-Correlation-ID', resolvedCorrelationID);
      }
    }

    if (shouldLogRequestReceived) {
      request.log?.info?.(
        {
          requestID,
          correlationID: resolvedCorrelationID || undefined,
        },
        `client-info: request received — ${request.method} ${request.url}`,
      );
    }

    // Build the frozen clientInfo snapshot once, after resolution.
    // (clientIP may have been overridden above with the forwarded original IP.)
    request.clientInfo = {
      requestID,
      correlationID: resolvedCorrelationID,
      isFromSSRServerAPICall,
      connectionIP: request.connectionIP,
      clientIP: request.clientIP,
      userAgent,
      isIPFromHeader,
      isUserAgentFromHeader,
    };
    Object.freeze(request.clientInfo);
  });
}
